import { appApi, idTag, orgTag } from '@/app/redux/appApi';
import {
  CreateWordMatInput,
  UpdateWordMatInput,
  WordMat,
} from '@/common/types/wordMat';
import api from '@/db/apiClient';

const wordMatsApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getWordMatsForOrganisation: build.query<WordMat[], string>({
      query: (orgId) => api.asEducator.getWordMatsForOrganisation(orgId),
      providesTags: (result, _err, orgId) =>
        result
          ? [
              orgTag('WordMats', orgId),
              ...result.map((wm) => idTag('WordMats', wm.id)),
            ]
          : [orgTag('WordMats', orgId)],
    }),

    createWordMat: build.mutation<WordMat, CreateWordMatInput>({
      query: (data) => api.asEducator.createWordMat(data),
      invalidatesTags: (_res, _err, data) => [
        orgTag('WordMats', data.organisationId),
      ],
    }),

    updateWordMat: build.mutation<
      null,
      { orgId: string; id: string; updates: UpdateWordMatInput }
    >({
      query: ({ id, updates }) => api.asEducator.updateWordMat(id, updates),
      onQueryStarted: async (
        { orgId, id, updates },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          wordMatsApi.util.updateQueryData(
            'getWordMatsForOrganisation',
            orgId,
            (draft) => {
              const wm = draft.find((x) => x.id === id);
              if (wm) Object.assign(wm, updates, { updatedAt: Date.now() });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deleteWordMat: build.mutation<null, { orgId: string; id: string }>({
      query: async ({ id }) => api.asEducator.deleteWordMat(id),
      onQueryStarted: async ({ orgId, id }, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          wordMatsApi.util.updateQueryData(
            'getWordMatsForOrganisation',
            orgId,
            (draft) => {
              const idx = draft.findIndex((wm) => wm.id === id);
              if (idx !== -1) draft.splice(idx, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),

  overrideExisting: false,
});

export default wordMatsApi;
export const {
  useGetWordMatsForOrganisationQuery,
  useCreateWordMatMutation,
  useUpdateWordMatMutation,
  useDeleteWordMatMutation,
} = wordMatsApi;
